import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock, Plus, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/config/constants";

const CalendarPage = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [calendarData, setCalendarData] = useState({ days: {} });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/calendar?year=${year}&month=${month}`);
        setCalendarData(response.data);
      } catch (error) { toast.error("Failed to load calendar data"); }
      finally { setLoading(false); }
    };
    fetchCalendar();
  }, [year, month]);

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const navigateMonth = (direction) => {
    setSelectedDay(null);
    if (direction === "prev") { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); }
    else { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); }
  };

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const getEntriesForDay = (day) => {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.days[key] || [];
  };
  const selectedEntries = selectedDay ? getEntriesForDay(selectedDay) : [];

  return (
    <div className="max-w-5xl mx-auto page-content" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div><h1 className="font-heading text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">Calendar</h1><p className="text-xs sm:text-base text-white/80 mt-1">Browse your nature journal by date</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateMonth("prev")} className="bg-[#55624F] border-[#55624F] text-white h-8 w-8 sm:h-10 sm:w-10" data-testid="prev-month-btn"><CaretLeft size={16} /></Button>
          <span className="font-['Manrope'] text-sm sm:text-lg font-semibold text-white min-w-[140px] sm:min-w-[180px] text-center" data-testid="current-month">{monthNames[month - 1]} {year}</span>
          <Button variant="outline" size="icon" onClick={() => navigateMonth("next")} className="bg-[#55624F] border-[#55624F] text-white h-8 w-8 sm:h-10 sm:w-10" data-testid="next-month-btn"><CaretRight size={16} /></Button>
        </div>
      </div>

      <Card className="bg-white border-[#D2C2B3] mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">{dayNames.map(d => (<div key={d} className="text-center text-xs font-medium text-white/70 py-1">{d}</div>))}</div>
          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} className="aspect-square" />;
              const entries = getEntriesForDay(day);
              const hasEntries = entries.length > 0;
              const isToday = isCurrentMonth && today.getDate() === day;
              const isSelected = selectedDay === day;
              return (
                <button key={day} onClick={() => setSelectedDay(isSelected ? null : day)} className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-all relative ${isSelected ? "bg-[#55624F] text-white" : isToday ? "bg-[#EAE7DE] ring-2 ring-[#55624F] text-[#2E332B]" : hasEntries ? "bg-[#EAE7DE] hover:bg-[#D2C2B3] text-[#2E332B]" : "hover:bg-[#F5F4F0] text-[#7C8068]"}`} data-testid={`day-${day}`}>
                  <span className={`text-sm font-medium ${isSelected ? "text-white" : ""}`}>{day}</span>
                  {hasEntries && (<div className="flex gap-0.5 mt-0.5">{entries.slice(0, 3).map((_, i) => (<div key={i} className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-[#C25A3C]"}`} />))}</div>)}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {selectedDay && (
        <div data-testid="day-entries">
          <h2 className="font-heading text-xl font-semibold text-white mb-3">{monthNames[month - 1]} {selectedDay}, {year}<span className="text-sm font-normal text-white/70 ml-2">{selectedEntries.length} {selectedEntries.length === 1 ? "entry" : "entries"}</span></h2>
          {selectedEntries.length === 0 ? (
            <Card className="bg-[#EAE7DE] border-[#D2C2B3]"><CardContent className="p-6 text-center"><p className="text-[#7C8068]">No entries on this day</p><Button onClick={() => navigate("/entry/new")} className="mt-3 bg-[#55624F] hover:bg-[#424D3D] text-white"><Plus size={16} className="mr-1" /> Create Entry</Button></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {selectedEntries.map(entry => (
                <Card key={entry.id} className="bg-white border-[#D2C2B3] cursor-pointer card-hover" onClick={() => navigate(`/entry/${entry.id}`)} data-testid={`calendar-entry-${entry.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {entry.photos?.[0]?.storage_path && (<img src={`${API}/photos/${entry.photos[0].storage_path}`} alt={entry.title} className="w-16 h-16 object-cover rounded-lg" />)}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-[#2E332B] truncate">{entry.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-[#7C8068]">
                        {entry.location && (<span className="flex items-center gap-1"><MapPin size={12} /> {entry.location}</span>)}
                        <span className="flex items-center gap-1"><Clock size={12} /> {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {entry.tags?.length > 0 && (<div className="flex gap-1 mt-1">{entry.tags.slice(0, 3).map(tag => (<Badge key={tag} className="bg-[#EAE7DE] text-[#55624F] text-xs">{tag}</Badge>))}</div>)}
                    </div>
                    {entry.species_identified?.[0] && (<Badge className="bg-[#C25A3C] text-white text-xs shrink-0">{entry.species_identified[0].species_name}</Badge>)}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
